import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { GitBranchPlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Field, Panel, StatusBadge } from "@/components/app/common";
import { AuditMeta, AuditPanel } from "@/components/app/AuditPanel";
import { DocumentsPanel } from "@/components/app/DocumentsPanel";
import { RevisionEditor } from "@/components/design/RevisionEditor";
import { StubBanner } from "@/pages/Designs";
import { useMe } from "@/components/app/MeContext";
import { useFetch } from "@/hooks/useFetch";
import { api, errMsg } from "@/lib/api";

export default function DesignDetail() {
  const { id } = useParams();
  const { can } = useMe();
  const { data: d, reload, error } = useFetch(`/designs/${id}`);
  const [selected, setSelected] = useState(null);
  const [tick, setTick] = useState(0);
  if (error) return <div className="text-red-600 text-sm">{error}</div>;
  if (!d) return <div className="text-sm text-slate-500">Loading…</div>;
  const refresh = () => { reload(); setTick((t) => t + 1); };
  const rev = d.revisions.find((r) => r.id === selected) || d.revisions[0];
  const hasDraft = d.revisions.some((r) => r.status === "draft");
  const newRev = async () => {
    try { const r = await api.post(`/designs/${d.id}/revisions`); setSelected(r.data.id); refresh(); toast.success(`R${r.data.revision_no} created`); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="design-detail-page">
      <Link to="/designs" className="text-xs text-slate-500 hover:underline">← Design Hub</Link>
      <div className="flex items-center gap-3 mt-1 mb-1">
        <h1 className="text-2xl font-bold tracking-tight"><span className="font-mono">{d.design_no}</span> · {d.title}</h1>
        <StatusBadge status={d.status} testid="design-status-badge" />
      </div>
      <div className="mb-4"><AuditMeta record={d} /></div>
      <StubBanner />
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 mb-4">
        <Panel title="Record" className="lg:col-span-1" testid="design-record-panel">
          <div className="space-y-3">
            <Field k="Customer" v={<Link className="text-sky-700" to={`/masters/customers?open=${d.customer_id}`}>{d.customer_snapshot.name}</Link>} />
            <Field k="Related order" v={d.order_ref && <Link className="text-sky-700 font-mono" to={`/orders/${d.order_ref.id}`}>{d.order_ref.order_no}</Link>} />
            <Field k="Application" v={d.application} />
            <Field k="Notes" v={d.notes} />
          </div>
        </Panel>
        <Panel title="Revisions" className="lg:col-span-3" testid="design-revisions-panel"
          actions={can("write") && !hasDraft && <Button size="sm" variant="outline" onClick={newRev} data-testid="new-revision-btn"><GitBranchPlus className="h-4 w-4 mr-1" />New revision</Button>}>
          <div className="flex flex-wrap gap-2 mb-4">
            {d.revisions.map((r) => (
              <button key={r.id} onClick={() => setSelected(r.id)} data-testid={`revision-tab-R${r.revision_no}`}
                className={`px-3 py-1.5 rounded border text-xs font-mono flex items-center gap-2 ${rev.id === r.id ? "border-sky-500 bg-sky-50 text-sky-800" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
                R{r.revision_no} <span className="uppercase">{r.status}</span>{d.latest_approved_revision_id === r.id && <span className="text-emerald-700">latest</span>}
              </button>
            ))}
          </div>
          <RevisionEditor key={`${rev.id}-${rev.updated_at}-${rev.status}`} design={d} rev={rev} onSaved={refresh} />
        </Panel>
      </div>
      <Tabs defaultValue="orders" className="bg-white border border-slate-200 rounded-md">
        <TabsList className="w-full justify-start rounded-none border-b border-slate-200 bg-slate-50 h-auto flex-wrap p-1">
          <TabsTrigger value="orders" data-testid="design-tab-orders">Linked orders ({d.linked_orders.length})</TabsTrigger>
          <TabsTrigger value="docs" data-testid="design-tab-documents">Documents & specs</TabsTrigger>
          <TabsTrigger value="audit" data-testid="design-tab-audit">Audit</TabsTrigger>
        </TabsList>
        <div className="p-4">
          <TabsContent value="orders">
            {d.linked_orders.map((o) => (
              <div key={o.id} className="flex flex-wrap items-center gap-3 py-2 border-b border-slate-100" data-testid={`linked-order-${o.order_no}`}>
                <Link to={`/orders/${o.id}`} className="font-mono text-sky-700">{o.order_no}</Link>
                <span className="text-xs font-mono">uses R{o.design_snapshot?.revision_no}</span>
                <StatusBadge status={o.status} />
                {o.design_revision_id !== d.latest_approved_revision_id && <span className="text-xs text-amber-700">newer approved revision available</span>}
              </div>
            ))}
            {d.linked_orders.length === 0 && <p className="text-sm text-slate-500">No orders reference this design yet. Link it from an order page (approved revisions only).</p>}
          </TabsContent>
          <TabsContent value="docs"><DocumentsPanel entityType="design" entityId={d.id} onChange={() => setTick((t) => t + 1)} /></TabsContent>
          <TabsContent value="audit"><AuditPanel entityId={d.id} refreshKey={tick} /></TabsContent>
        </div>
      </Tabs>
    </div>
  );
}
